import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { getItemUniqueKey, getPackageId } from '../accommodation/cart'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'
import { addHours, dateIsAfter, dateIsBefore, isBookingWithinDateRange } from 'lib/datetime/dateUtils'
import { min, sum, uniqueBy } from 'lib/array/arrayUtils'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import { isPostPurchaseAncillaryCartMode } from 'checkout/selectors/payment/isPostPurchaseAncillaryPayment'
import type { IntlShape } from 'react-intl'

// NB: You can use the returned `RawPropertyRate` for inclusions and bundle information, but you
// mustn't use it for pricing, as the raw rates stored in state have not been enriched with flights.
function findUnifiedBedbankRateForCheckoutItem(
  item: App.Checkout.BedbankHotelItem,
  offer: App.BedbankOffer,
  propertyRatesByKey: App.OfferState['propertyRates'] | undefined,
): App.RawBedbankPropertyRate | undefined {
  if (!propertyRatesByKey || !item.isAccomBundle) {
    return undefined
  }

  const allRates = Object.values(propertyRatesByKey)
    .flatMap(state =>
      (state?.rooms ?? [])
        .filter((entry): entry is App.RawAvailablePropertyRoomRates => 'rates' in entry)
        .flatMap(entry => Object.values(entry.rates)),
    )
    .filter((rate): rate is App.RawBedbankPropertyRate => rate.service.type === 'bedbank')

  const ref = item.propertyRateRef
  if (ref?.serviceType === 'bedbank') {
    const refPropertyRates = allRates.filter(rate =>
      rate.propertyId === ref.propertyId &&
      rate.roomId === ref.roomId,
    )
    if (refPropertyRates.length) {
      const scopedRates = ref.bundleId
        ? refPropertyRates.filter(rate => rate.bundle?.bundleId === ref.bundleId)
        : refPropertyRates

      if (ref.groupKey) {
        const byGroupKey = scopedRates.find(rate => rate.groupKey === ref.groupKey)
        if (byGroupKey) return byGroupKey
      }

      const byRateId = scopedRates.find(rate => rate.service.rateId === ref.serviceRateId || rate.rateId === ref.serviceRateId)
      if (byRateId) return byRateId
    }
  }

  const propertyRates = allRates.filter(rate => rate.propertyId === offer.property.id && rate.roomId === item.roomId)
  if (!propertyRates.length) {
    return undefined
  }

  if (item.isAccomBundle) {
    const bundledRates = propertyRates.filter(rate => rate.bundle?.bundleId === item.isAccomBundle)
    const bundledRateById = bundledRates.find(rate => rate.service.rateId === item.roomRateId || rate.rateId === item.roomRateId)
    if (bundledRateById) return bundledRateById
    if (bundledRates.length === 1) return bundledRates[0]
  }

  return propertyRates.find(rate => rate.service.rateId === item.roomRateId || rate.rateId === item.roomRateId)
}

function dummyBedbankItemView(
  intl: IntlShape,
  item: App.Checkout.BedbankHotelItem,
  kind: 'bedbank',
  offer?: App.BedbankOffer,
): App.Checkout.BedbankAccommodationItemView {
  return {
    kind,
    packageId: getPackageId(item),
    uniqueKey: getItemUniqueKey(item),
    item,
    image: {},
    primaryTitle: '',
    occupancy: { adults: 0, children: 0, infants: 0 },
    removeLabel: intl.formatMessage({
      id: 'kKF0I_jMAhUr5_wOULZ8',
      defaultMessage: 'Remove',
      description: 'Remove button label for bedbank accommodation item in checkout',
    }),
    soldOut: true,
    loyalty: {
      productType: getLoyaltyProductType(offer),
    },
    title: '',
    totals: {
      price: 0,
      memberPrice: 0,
      value: 0,
      surcharge: 0,
      taxesAndFees: 0,
      memberValue: 0,
      extraGuestSurcharge: 0,
    },
    propertyId: offer?.property.id,
  }
}

export function getBedbankItemView(
  intl: IntlShape,
  item: App.Checkout.BedbankHotelItem,
  // This is temporary until LPP member pricing is fully released
  luxPlusLPPEnabled: boolean,
  session: App.BedbankEnquiry | null,
  offer?: App.BedbankOffer,
  rate?: App.BedbankRate,
  propertyRatesByKey?: App.OfferState['propertyRates'],
  postPurchase?: App.CheckoutCartMode,
  existingOrder?: App.Order,
  ratesFetched?: boolean,
): App.WithDataStatus<App.Checkout.BedbankAccommodationItemView> {
  if (!offer) {
    return {
      hasRequiredData: false,
      data: dummyBedbankItemView(intl, item, 'bedbank', offer),
    }
  }

  // no rate means no availability
  if (!rate) {
    return {
      hasRequiredData: isPostPurchaseAncillaryCartMode(postPurchase) || (postPurchase === 'change-dates' || postPurchase === 'change-package') ? !!ratesFetched : false,
      data: dummyBedbankItemView(intl, item, 'bedbank', offer),
    }
  }

  if (session?.occupancyPricing && session?.totals) {
    rate.occupancyPricing = session.occupancyPricing
    rate.totals = session.totals
  }

  if (session?.cancellationPolicies && rate.refundable) {
    const oldDeadline = min(rate.cancellationPolicies.map(cp => new Date(cp.start)))
    const newDeadline = min(session.cancellationPolicies.map(cp => new Date(cp.start)))
    if (oldDeadline && newDeadline && !rate.cancellationPolicyChanged) {
      // if new deadline is at least 1 hr earlier
      rate.cancellationPolicyChanged = newDeadline < addHours(oldDeadline, -1)
    }

    rate.cancellationPolicies = session.cancellationPolicies
  }

  if (session) {
    // should always override including undefine
    rate.paymentScheduleInfo = session.paymentScheduleInfo
  }

  const pkg = rate.room

  const hasPromo = offer.promotions.length > 0 && item.duration >= offer.promotions[0].los

  const todayWithinOfferPeriod = hasPromo && dateIsAfter(new Date(), new Date(offer.promotions[0].periodSchedule.from)) &&
    dateIsBefore(new Date(), new Date(offer.promotions[0].periodSchedule.to))

  const includePromoInclusions = (offer.type === OFFER_TYPE_BED_BANK) ?
    hasPromo && todayWithinOfferPeriod &&
    isBookingWithinDateRange(item.checkIn, item.checkOut, offer.promotions[0].travelPeriod.from, offer.promotions[0].travelPeriod.to) :
    true

  const unifiedRate = findUnifiedBedbankRateForCheckoutItem(item, offer, propertyRatesByKey)
  function getInclusions(): { inclusions: Array<App.PackageInclusion> | undefined, bundleInclusions: Array<App.PackageInclusion> | undefined } {
    const promotionInclusions = includePromoInclusions
      ? offer?.promotions?.find(p => rate?.promotionId === p.id)?.inclusions
      : undefined
    const rateInclusions = rate?.inclusions
      ? rate.inclusions.map<App.PackageInclusion>(item => ({
          id: item.id,
          description: item.description,
        }))
      : undefined
    const unifiedInclusions = unifiedRate?.inclusions ?? []
    const unifiedBundleInclusions = unifiedRate?.bundleInclusions ?? []
    const legacyInclusions = promotionInclusions ?? rateInclusions

    // Keep legacy bedbank behavior when no unified match exists.
    // Only append unified/property-rate inclusions when available.
    const inclusions = (!unifiedInclusions.length && !unifiedBundleInclusions.length)
      ? legacyInclusions
      : uniqueBy([
          ...(legacyInclusions ?? []),
          ...unifiedInclusions,
        ], inclusion => `${inclusion.id ?? ''}::${inclusion.description ?? ''}`)

    const bundleInclusions = unifiedBundleInclusions.length
      ? uniqueBy(unifiedBundleInclusions, inclusion => `${inclusion.id ?? ''}::${inclusion.description ?? ''}`)
      : undefined

    return { inclusions, bundleInclusions }
  }

  const itemTotals = getBedbankItemViewTotals(rate, item.occupancy, luxPlusLPPEnabled)

  const soldOut = !itemTotals.price

  if ((postPurchase == 'change-dates' || postPurchase === 'change-package') && 'orderItemId' in item && existingOrder) {
    const orderItem = existingOrder.bedbankItems.find(orderItem => orderItem.id === item.orderItemId)

    if (orderItem) {
      const activeRooms = orderItem.rooms.filter(room => room.status === 'booked')
      const roomsLength = activeRooms.length
      // subtracting old room price from new room, each room is a checkout item so we need to divide sum by room.length
      const orderTotal = Math.round(sum(activeRooms.map(room => room.price)) * 100) / roomsLength

      itemTotals.paidPrice = sum(activeRooms.map(room => room.price))

      itemTotals.newPrice = itemTotals.price

      const priceDiff = Number(((Math.round(itemTotals.price * 100) - orderTotal) / 100).toFixed(2))
      const memberPriceDiff = luxPlusLPPEnabled ? Number(((Math.round(itemTotals.memberPrice * 100) - orderTotal) / 100).toFixed(2)) : 0

      itemTotals.price = priceDiff >= 0 ? priceDiff : 0
      itemTotals.memberPrice = memberPriceDiff >= 0 ? memberPriceDiff : 0

      // clear those prices that we don't want to display
      itemTotals.value = 0
      itemTotals.taxesAndFees = 0
      itemTotals.otherFees!.propertyFees = 0
    }
  }

  const data: App.Checkout.BedbankAccommodationItemView = {
    title: `${pkg?.name}: ${offer.name}`,
    kind: 'bedbank',
    packageId: item.roomId,
    uniqueKey: item.roomId,
    item,
    primaryTitle: pkg?.name ?? '',
    image: pkg?.images[0] ?? offer.image,
    occupancy: item.occupancy,
    rate,
    removeLabel: intl.formatMessage({
      id: 'YwsHiYKEsfSqYy2zz6Yw',
      defaultMessage: 'Remove room',
      description: 'Remove button label for bedbank room item in checkout',
    }),
    totals: itemTotals,
    ...getInclusions(),
    soldOut,
    hasMobilePromotion: rate.mobilePromotion,
    loyalty: {
      productType: getLoyaltyProductType(offer),
      groupKey: item.sessionId,
    },
    mx: rate.mx,
    propertyId: offer.property.id,
  }

  const hasRequiredData = true

  if (!session) {
    return {
      hasRequiredData: ['change-dates', 'change-package', 'payment-schedule-balance'].includes(postPurchase ?? ''),
      data,
    }
  }

  return {
    hasRequiredData,
    data,
  }
}

export function getBedbankItemViewTotals(rate: App.BedbankRate, occupancy: App.Occupants, luxPlusLPPEnabled: boolean) {
  const pricing = rate.occupancyPricing.find(pricing =>
    pricing.occupancy === generateOccupancyStringByRoom(occupancy),
  ) || rate.occupancyPricing[0]

  const memberExclusive = pricing.memberExclusive ?? 0

  // The rounding and the `toFixed` call are to correct for floating point errors
  const price = Number(((Math.round(pricing.exclusive * 100) + Math.round(pricing.taxesAndFees * 100)) / 100).toFixed(2))
  const memberPrice = memberExclusive > 0 ? Number(((Math.round(memberExclusive * 100) + Math.round(pricing.taxesAndFees * 100)) / 100).toFixed(2)) : 0

  const itemTotals: App.Checkout.ItemViewTotals = {
    price,
    memberPrice: luxPlusLPPEnabled ? memberPrice : 0,
    value: pricing.inclusive,
    surcharge: 0,
    taxesAndFees: pricing.taxesAndFees ?? 0,
    otherFees: {
      propertyFees: pricing.propertyFees ?? 0,
    },
    paidPrice: 0,
    newPrice: 0,
    newSurcharge: 0,
    newExtraGuestSurcharge: 0,
    memberValue: 0,
    extraGuestSurcharge: 0,
  }

  return itemTotals
}
